import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { InternalRequestDefaults, InternalRequestNodeData } from "@gestionalo/enterprise-graph";
import type { AvailableField } from "@/lib/productGraph";
import {
  getCertificateRequestIntents,
  type CertificateRequestIntentDto,
} from "@/services/certificateRequestIntentsService";
import {
  getCertificatePricingCatalog,
  type CertificatePricingCatalogItem,
} from "@/services/certificatePricingCatalogService";
import { uploadProductTemplate } from "@/services/platformEnterprisesService";

const FLOW_KEY_LABELS: Record<string, string> = {
  certificate_catalog: "Certificados",
  enterprise_catalog: "Productos de empresa",
  open_company: "Apertura de empresa",
};

interface Props {
  data: InternalRequestNodeData;
  onChange: (patch: Partial<InternalRequestNodeData>) => void;
  enterpriseId: string;
  productId: string;
  availableFields?: AvailableField[];
}

export function InternalRequestEditor({ data, onChange, enterpriseId, productId, availableFields }: Props): JSX.Element {
  const { t } = useTranslation();
  const [intents, setIntents] = useState<CertificateRequestIntentDto[]>([]);
  const [catalog, setCatalog] = useState<CertificatePricingCatalogItem[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getCertificateRequestIntents().then(setIntents).catch(() => {});
    getCertificatePricingCatalog().then(setCatalog).catch(() => {});
  }, []);

  // Map slug → displayName from pricing catalog
  const productDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of catalog) {
      if (item.displayName) map.set(item.targetType, item.displayName);
    }
    return map;
  }, [catalog]);

  // Distinct flowKeys from intents
  const flowKeys = useMemo(() => {
    const keys = [...new Set(intents.map((i) => i.flowKey))];
    return keys.sort();
  }, [intents]);

  // Products matching the selected flowKey (use intents as source of truth)
  const matchingProducts = useMemo(() => {
    if (!data.productFilter?.flowKey) return [];
    return intents.filter((i) => i.flowKey === data.productFilter.flowKey);
  }, [data.productFilter?.flowKey, intents]);

  const selectedTargetTypes = new Set(data.productFilter?.targetTypes ?? []);

  const toggleTargetType = (targetType: string, checked: boolean): void => {
    const current = data.productFilter?.targetTypes ?? [];
    const next = checked
      ? [...current, targetType]
      : current.filter((t) => t !== targetType);
    onChange({ productFilter: { ...data.productFilter, targetTypes: next } });
  };

  const defaults = data.defaults ?? {};

  function updateDefaults(patch: Partial<InternalRequestDefaults>): void {
    onChange({ defaults: { ...defaults, ...patch } });
  }

  async function handleTemplateUpload(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadProductTemplate(enterpriseId, productId, file);
      updateDefaults({
        specialFormatTemplateUrl: result.url,
        specialFormatTemplateFilename: result.filename,
      });
    } catch {
      // Upload failed — user can retry
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function removeTemplate(): void {
    updateDefaults({
      specialFormatTemplateUrl: undefined,
      specialFormatTemplateFilename: undefined,
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.internalRequestLabel")}
          <span className="ml-1 text-destructive">*</span>
        </Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.internalRequestDescription")}
        </Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.internalRequestFlowKey")}
          <span className="ml-1 text-destructive">*</span>
        </Label>
        <Select
          value={data.productFilter?.flowKey ?? ""}
          onValueChange={(value) =>
            onChange({
              productFilter: {
                ...data.productFilter,
                flowKey: value,
                targetTypes: [],
              },
            })
          }
        >
          <SelectTrigger>
            <SelectValue
              placeholder={t(
                "enterpriseProductBuilder.internalRequestFlowKeyPlaceholder"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {flowKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {FLOW_KEY_LABELS[key] ?? key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {data.productFilter?.flowKey && matchingProducts.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.internalRequestTargetTypes")}
          </Label>
          <div className="space-y-2 rounded-md border p-3">
            {matchingProducts.map((intent) => (
              <label
                key={intent.slug}
                className="flex items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={selectedTargetTypes.has(intent.slug)}
                  onCheckedChange={(checked) =>
                    toggleTargetType(intent.slug, checked === true)
                  }
                />
                <span>{productDisplayNames.get(intent.slug) ?? intent.slug}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t("enterpriseProductBuilder.internalRequestTargetTypesHint")}
          </p>
        </div>
      )}

      {/* ── Valores predeterminados ── */}
      <div className="space-y-3 border-t pt-4 mt-4">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Valores predeterminados
        </Label>

        {/* Fulfillment mode */}
        <div className="space-y-1">
          <Label className="text-sm">Modo de entrega</Label>
          <Select
            value={defaults.fulfillmentMode ?? ""}
            onValueChange={(v) =>
              updateDefaults({ fulfillmentMode: (v || undefined) as InternalRequestDefaults['fulfillmentMode'] })
            }
          >
            <SelectTrigger><SelectValue placeholder="No predeterminado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="digital_only">Solo digital</SelectItem>
              <SelectItem value="physical_only">Solo físico</SelectItem>
              <SelectItem value="digital_and_physical">Digital y físico</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Period */}
        <div className="space-y-1">
          <Label className="text-sm">Período a certificar</Label>
          <Select
            value={defaults.periodMonths?.toString() ?? ""}
            onValueChange={(v) =>
              updateDefaults({ periodMonths: v ? (Number(v) as 6 | 12 | 24) : undefined })
            }
          >
            <SelectTrigger><SelectValue placeholder="No predeterminado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Últimos 6 meses</SelectItem>
              <SelectItem value="12">Últimos 12 meses</SelectItem>
              <SelectItem value="24">Últimos 24 meses</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Presenting entity as enterprise */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="presenting-entity-enterprise"
            checked={defaults.presentingEntityAsEnterprise ?? false}
            onCheckedChange={(checked) =>
              updateDefaults({ presentingEntityAsEnterprise: checked === true })
            }
          />
          <Label htmlFor="presenting-entity-enterprise" className="text-sm cursor-pointer">
            Preseleccionar entidad como nombre de empresa
          </Label>
        </div>

        {/* Template upload */}
        <div className="space-y-1">
          <Label className="text-sm">Plantilla de formato (PDF)</Label>
          {defaults.specialFormatTemplateUrl ? (
            <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <span className="flex-1 truncate">{defaults.specialFormatTemplateFilename ?? 'template.pdf'}</span>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={removeTemplate}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 transition-colors">
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span>{uploading ? 'Subiendo...' : 'Seleccionar archivo PDF'}</span>
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => void handleTemplateUpload(e)}
                disabled={uploading}
              />
            </label>
          )}
        </div>
      </div>

      {/* ── Condicion de activacion ── */}
      {availableFields && availableFields.length > 0 && (
        <div className="space-y-3 border-t pt-4 mt-4">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("enterpriseProductBuilder.internalRequestConditionTitle")}
          </Label>
          <p className="text-[10px] text-muted-foreground">
            {t("enterpriseProductBuilder.internalRequestConditionHint")}
          </p>

          <div className="space-y-1.5">
            <Label className="text-xs">
              {t("enterpriseProductBuilder.internalRequestConditionField")}
            </Label>
            <Select
              value={data.condition?.field ?? "__none__"}
              onValueChange={(v) => {
                if (v === "__none__") {
                  onChange({ condition: undefined });
                } else {
                  onChange({ condition: { field: v, values: data.condition?.values ?? [] } });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  {t("enterpriseProductBuilder.internalRequestConditionNone")}
                </SelectItem>
                {availableFields
                  .filter((f) => f.type === "select" && f.options?.length)
                  .map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {data.condition?.field && (() => {
            const condField = availableFields.find((f) => f.key === data.condition?.field);
            if (!condField?.options?.length) return null;
            const selectedValues = new Set(data.condition?.values ?? []);
            return (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  {t("enterpriseProductBuilder.internalRequestConditionValues")}
                </Label>
                <div className="space-y-2 rounded-md border p-3">
                  {condField.options.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={selectedValues.has(opt.value)}
                        onCheckedChange={(checked) => {
                          const current = data.condition?.values ?? [];
                          const next = checked
                            ? [...current, opt.value]
                            : current.filter((v) => v !== opt.value);
                          onChange({ condition: { ...data.condition!, values: next } });
                        }}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
